import React, { useEffect, useMemo, useState } from "react"
import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonMenuButton,
  IonModal,
  IonNote,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRange,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
  RefresherEventDetail,
  useIonViewWillEnter,
} from "@ionic/react"

import API, {
  Evaluation,
  EvaluationAnswer,
  EvaluationQuestion,
  EvaluationTemplate,
  EvaluationUser,
} from "../helpers/API"
import "./Evaluations.scss"

type EvaluationTab = "new" | "list"
type AnswerMap = { [questionId: number]: EvaluationAnswer }

const statusLabel = (status: string) => {
  if (status === "submitted") return "Inviata"
  if (status === "locked") return "Bloccata"
  return "Bozza"
}

const fullName = (user?: EvaluationUser) => {
  if (!user) return "Utente non disponibile"
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim()
  return name || user.username
}

const todayIsoDate = () => new Date().toISOString().slice(0, 10)

const toAnswerMap = (questions: EvaluationQuestion[], sourceAnswers: EvaluationAnswer[] = []) => {
  const map: AnswerMap = {}
  questions.forEach((question) => {
    const existing = sourceAnswers.find((answer) => answer.question === question.id)
    // Xe el default: voto medio e campi puliti, cussì el docente parte senza casino.
    map[question.id] = {
      question: question.id,
      rating_value: question.question_type === "rating" ? existing?.rating_value || 3 : null,
      boolean_value:
        question.question_type === "boolean" ? existing?.boolean_value ?? false : null,
      text_value: existing?.text_value || "",
    }
  })
  return map
}

const Evaluations: React.FC = () => {
  const [activeTab, setActiveTab] = useState<EvaluationTab>("new")
  const [templates, setTemplates] = useState<EvaluationTemplate[]>([])
  const [trainees, setTrainees] = useState<EvaluationUser[]>([])
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [traineeSearchText, setTraineeSearchText] = useState<string>("")
  const [isTraineePickerOpen, setIsTraineePickerOpen] = useState<boolean>(false)
  const [evaluationSearchText, setEvaluationSearchText] = useState<string>("")
  const [templateSearchText, setTemplateSearchText] = useState<string>("")
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState<boolean>(false)
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<number | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>()
  const [selectedTraineeId, setSelectedTraineeId] = useState<number>()
  const [periodStart, setPeriodStart] = useState<string>(todayIsoDate())
  const [periodEnd, setPeriodEnd] = useState<string>(todayIsoDate())
  const [notes, setNotes] = useState<string>("")
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [isLoading, setIsLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState<string>("")

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId),
    [templates, selectedTemplateId]
  )

  const normalizedTemplateSearchText = templateSearchText.trim().toLowerCase()
  const filteredTemplates = templates.filter((template) => {
    if (!normalizedTemplateSearchText) return true
    const haystack = `${template.title} ${template.description || ""} v${template.version}`.toLowerCase()
    return haystack.includes(normalizedTemplateSearchText)
  })

  const normalizedTraineeSearchText = traineeSearchText.trim().toLowerCase()
  const filteredTrainees = trainees.filter((trainee) => {
    if (!normalizedTraineeSearchText) return true
    const haystack = `${fullName(trainee)} ${trainee.username} ${trainee.email}`.toLowerCase()
    return haystack.includes(normalizedTraineeSearchText)
  })

  const normalizedEvaluationSearchText = evaluationSearchText.trim().toLowerCase()
  const filteredEvaluations = evaluations.filter((evaluation) => {
    if (!normalizedEvaluationSearchText) return true
    const haystack = `${fullName(evaluation.trainee_detail)} ${evaluation.template_detail?.title || ""} ${evaluation.status} ${evaluation.period_start || ""} ${evaluation.period_end || ""}`.toLowerCase()
    return haystack.includes(normalizedEvaluationSearchText)
  })

  const selectedTrainee = trainees.find((trainee) => trainee.id === selectedTraineeId)
  const draftCount = evaluations.filter((evaluation) => evaluation.status === "draft").length

  useIonViewWillEnter(() => {
    loadData()
  })

  useEffect(() => {
    if (!selectedTemplateId && templates.length > 0) {
      setSelectedTemplateId(templates[0].id)
    }
  }, [templates, selectedTemplateId])

  useEffect(() => {
    if (!selectedTraineeId && trainees.length > 0) {
      setSelectedTraineeId(trainees[0].id)
    }
  }, [trainees, selectedTraineeId])

  useEffect(() => {
    if (selectedTemplate && selectedEvaluationId === null) {
      setAnswers(toAnswerMap(selectedTemplate.questions))
    }
  }, [selectedTemplate, selectedEvaluationId])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [templateData, traineeData, evaluationData] = await Promise.all([
        API.getEvaluationTemplates(),
        API.getEvaluationTrainees(),
        API.getEvaluations(),
      ])
      setTemplates(templateData)
      setTrainees(traineeData)
      setEvaluations(evaluationData)
    } catch (error) {
      setToastMessage("Non riesco a caricare le valutazioni.")
    } finally {
      setIsLoading(false)
    }
  }

  const refreshData = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      await loadData()
    } finally {
      event.detail.complete()
    }
  }

  const resetForm = () => {
    setSelectedEvaluationId(null)
    setPeriodStart(todayIsoDate())
    setPeriodEnd(todayIsoDate())
    setNotes("")
    // Gnanca magia: quando resetti, riparti dal primo template disponibile.
    const firstTemplate = templates[0]
    setSelectedTemplateId(firstTemplate?.id)
    if (firstTemplate) {
      setAnswers(toAnswerMap(firstTemplate.questions))
    }
  }

  const editDraft = (evaluation: Evaluation) => {
    if (evaluation.status !== "draft") {
      setToastMessage("Solo le bozze possono essere modificate dall'app.")
      return
    }
    const template = evaluation.template_detail || templates.find((item) => item.id === evaluation.template)
    setSelectedEvaluationId(evaluation.id)
    setSelectedTemplateId(evaluation.template)
    setSelectedTraineeId(evaluation.trainee)
    setPeriodStart(evaluation.period_start || "")
    setPeriodEnd(evaluation.period_end || "")
    setNotes(evaluation.notes || "")
    if (template) {
      setAnswers(toAnswerMap(template.questions, evaluation.answers))
    }
    setActiveTab("new")
    setToastMessage("Bozza caricata per la modifica.")
  }

  const updateAnswer = (question: EvaluationQuestion, value: unknown) => {
    const current = answers[question.id] || { question: question.id }
    const nextAnswer: EvaluationAnswer = {
      question: question.id,
      rating_value: null,
      boolean_value: null,
      text_value: "",
    }

    if (question.question_type === "rating") {
      nextAnswer.rating_value = Number(value)
    } else if (question.question_type === "boolean") {
      nextAnswer.boolean_value = value === true || value === "true"
    } else {
      nextAnswer.text_value = value ? String(value) : ""
    }

    setAnswers({ ...answers, [question.id]: { ...current, ...nextAnswer } })
  }

  const validateForm = () => {
    if (!selectedTemplateId || !selectedTraineeId || !selectedTemplate) {
      return "Scegli template e specializzando prima di salvare."
    }
    if (periodStart && periodEnd && periodEnd < periodStart) {
      return "La fine periodo non può essere prima dell'inizio."
    }

    const missingQuestion = selectedTemplate.questions.find((question) => {
      const answer = answers[question.id]
      if (!question.is_required) return false
      if (question.question_type === "rating") return !answer?.rating_value
      if (question.question_type === "boolean") return answer?.boolean_value === null || answer?.boolean_value === undefined
      return !answer?.text_value
    })

    if (missingQuestion) {
      return `Compila il campo obbligatorio: ${missingQuestion.text}`
    }
    return ""
  }

  const buildPayload = () => ({
    template: selectedTemplateId,
    trainee: selectedTraineeId,
    period_start: periodStart || null,
    period_end: periodEnd || null,
    notes: notes,
    answers: Object.values(answers),
  })

  const saveDraft = async () => {
    const validationMessage = validateForm()
    if (validationMessage) {
      setToastMessage(validationMessage)
      return null
    }

    setIsLoading(true)
    try {
      const payload = buildPayload()
      const saved = selectedEvaluationId
        ? await API.updateEvaluation(selectedEvaluationId, payload)
        : await API.createEvaluation(payload)
      setSelectedEvaluationId(saved.id)
      setEvaluations((current) => {
        const exists = current.some((evaluation) => evaluation.id === saved.id)
        return exists
          ? current.map((evaluation) => (evaluation.id === saved.id ? saved : evaluation))
          : [saved, ...current]
      })
      setToastMessage("Bozza valutazione salvata.")
      return saved
    } catch (error) {
      setToastMessage("Errore durante il salvataggio della valutazione.")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const submitEvaluation = async (evaluationId: number) => {
    setIsLoading(true)
    try {
      const submitted = await API.submitEvaluation(evaluationId)
      setEvaluations((current) =>
        current.map((evaluation) => (evaluation.id === submitted.id ? submitted : evaluation))
      )
      if (selectedEvaluationId === submitted.id) {
        resetForm()
        setActiveTab("list")
      }
      setToastMessage("Valutazione inviata.")
    } catch (error) {
      setToastMessage("Non riesco a inviare la valutazione.")
    } finally {
      setIsLoading(false)
    }
  }

  const saveAndSubmit = async () => {
    const saved = await saveDraft()
    if (saved) {
      await submitEvaluation(saved.id)
    }
  }

  const renderQuestion = (question: EvaluationQuestion) => {
    const answer = answers[question.id]

    return (
      <div className="evaluation-question" key={question.id}>
        <div className="evaluation-question-title">
          <h3>{question.text}</h3>
          {question.is_required && question.question_type === "text" ? (
            <IonBadge color="danger">Obbligatoria</IonBadge>
          ) : null}
        </div>
        {question.help_text ? <p>{question.help_text}</p> : null}

        {question.question_type === "rating" && (
          <IonItem lines="none">
            <IonLabel>Voto: {answer?.rating_value || 3}/5</IonLabel>
            <IonRange
              min={1}
              max={5}
              step={1}
              snaps={true}
              value={answer?.rating_value || 3}
              onIonChange={(event) => updateAnswer(question, event.detail.value)}
            />
          </IonItem>
        )}

        {question.question_type === "boolean" && (
          <IonRadioGroup
            className="evaluation-boolean-group"
            value={answer?.boolean_value ?? false}
            onIonChange={(event) => updateAnswer(question, event.detail.value)}
          >
            <IonItem lines="none">
              <IonRadio slot="start" value={true} />
              <IonLabel>Sì</IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonRadio slot="start" value={false} />
              <IonLabel>No</IonLabel>
            </IonItem>
          </IonRadioGroup>
        )}

        {question.question_type === "text" && (
          <IonItem>
            <IonLabel position="stacked">Risposta libera</IonLabel>
            <IonTextarea
              className="evaluation-answer-textarea"
              value={answer?.text_value || ""}
              autoGrow={true}
              rows={5}
              placeholder="Inserisci una nota per lo specializzando"
              onIonChange={(event) => updateAnswer(question, event.detail.value)}
            />
          </IonItem>
        )}
      </div>
    )
  }

  return (
    <IonPage>
      <IonLoading isOpen={isLoading} message={""} />
      <IonHeader className="evaluation-header">
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonTitle>Valutazioni</IonTitle>
        </IonToolbar>
        <IonToolbar className="evaluation-tabs-toolbar">
          <IonSegment
            value={activeTab}
            onIonChange={(event) => setActiveTab(event.detail.value as EvaluationTab)}
          >
            <IonSegmentButton value="new">
              <IonLabel>{selectedEvaluationId ? "Modifica" : "Nuova"}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="list">
              <div className="evaluation-tab-label-with-badge">
                <IonLabel>Elenco</IonLabel>
                {draftCount > 0 ? <IonBadge color="warning">{draftCount}</IonBadge> : null}
              </div>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent className={activeTab === "new" ? "evaluation-content has-evaluation-footer" : "evaluation-content"}>
        <IonRefresher slot="fixed" onIonRefresh={refreshData}>
          <IonRefresherContent />
        </IonRefresher>

        {activeTab === "new" && (
          <IonCard className="evaluation-card">
            <IonCardHeader>
              <IonCardTitle>
                {selectedEvaluationId ? "Modifica bozza" : "Nuova valutazione"}
              </IonCardTitle>
              <IonCardSubtitle>Compilazione docente</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <p className="evaluation-description">
                Seleziona specializzando e scheda, compila le domande e salva una
                bozza. Quando è tutto verificato puoi inviarla e bloccarla dalla
                modifica mobile.
              </p>

              <IonList>
                <IonItem
                  button={selectedEvaluationId === null}
                  disabled={selectedEvaluationId !== null}
                  onClick={() => setIsTemplatePickerOpen(true)}
                >
                  <IonLabel position="stacked">Scheda di valutazione</IonLabel>
                  <IonNote slot="end" className="evaluation-selected-note">
                    {selectedTemplate
                      ? `${selectedTemplate.title} v${selectedTemplate.version}`
                      : "Scegli una scheda"}
                  </IonNote>
                </IonItem>

                <IonItem
                  button={selectedEvaluationId === null}
                  disabled={selectedEvaluationId !== null}
                  onClick={() => setIsTraineePickerOpen(true)}
                >
                  <IonLabel position="stacked">Specializzando</IonLabel>
                  <IonNote slot="end">
                    {selectedTrainee ? fullName(selectedTrainee) : "Scegli uno specializzando"}
                  </IonNote>
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Inizio periodo</IonLabel>
                  <IonInput
                    type="date"
                    value={periodStart}
                    onIonChange={(event) => setPeriodStart(String(event.detail.value || ""))}
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Fine periodo</IonLabel>
                  <IonInput
                    type="date"
                    value={periodEnd}
                    onIonChange={(event) => setPeriodEnd(String(event.detail.value || ""))}
                  />
                </IonItem>

                <IonItem>
                  <IonLabel position="stacked">Note generali</IonLabel>
                  <IonTextarea
                    className="evaluation-notes-textarea"
                    value={notes}
                    autoGrow={true}
                    rows={5}
                    placeholder="Eventuali note generali sulla valutazione"
                    onIonChange={(event) => setNotes(event.detail.value || "")}
                  />
                </IonItem>
              </IonList>

              {selectedTemplate?.description ? (
                <IonNote className="evaluation-template-note">
                  {selectedTemplate.description}
                </IonNote>
              ) : null}

              {selectedTemplate?.questions.map(renderQuestion)}
            </IonCardContent>
          </IonCard>
        )}

        {activeTab === "list" && (
          <IonCard className="evaluation-card">
            <IonCardHeader>
              <IonCardTitle>Valutazioni esistenti</IonCardTitle>
              <IonCardSubtitle>Bozze e invii recenti</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonSearchbar
                value={evaluationSearchText}
                debounce={200}
                placeholder="Cerca per specializzando, scheda o stato"
                cancelButtonText="Annulla"
                onIonChange={(event) => setEvaluationSearchText(event.detail.value || "")}
              />
              {evaluations.length === 0 && (
                <div className="evaluation-empty">
                  Nessuna valutazione presente. Crea una prima bozza per iniziare.
                </div>
              )}
              {evaluations.length > 0 && filteredEvaluations.length === 0 && (
                <div className="evaluation-empty">
                  Nessuna valutazione trovata con questa ricerca.
                </div>
              )}
              <IonList>
                {filteredEvaluations.map((evaluation) => (
                  <IonItem key={evaluation.id} className="evaluation-list-item">
                    <IonLabel>
                      <h2>{fullName(evaluation.trainee_detail)}</h2>
                      <p>{evaluation.template_detail?.title || "Scheda valutazione"}</p>
                      <p>
                        {evaluation.period_start || "periodo non indicato"} — {evaluation.period_end || "periodo non indicato"}
                      </p>
                      <IonBadge color={evaluation.status === "draft" ? "warning" : "success"}>
                        {statusLabel(evaluation.status)}
                      </IonBadge>
                    </IonLabel>
                    {evaluation.status === "draft" && (
                      <div slot="end" className="evaluation-list-actions">
                        <IonButton fill="clear" onClick={() => editDraft(evaluation)}>
                          Modifica
                        </IonButton>
                        <IonButton
                          fill="outline"
                          color="success"
                          onClick={() => submitEvaluation(evaluation.id)}
                        >
                          Invia
                        </IonButton>
                      </div>
                    )}
                  </IonItem>
                ))}
              </IonList>
            </IonCardContent>
          </IonCard>
        )}
      </IonContent>
      {activeTab === "new" ? (
        <IonFooter className="evaluation-footer">
          <div className="evaluation-actions">
            {selectedEvaluationId ? (
              <IonButton fill="outline" onClick={resetForm}>
                Nuova scheda
              </IonButton>
            ) : null}
            <IonButton expand="block" onClick={saveDraft}>
              Salva bozza
            </IonButton>
            <IonButton expand="block" color="success" onClick={saveAndSubmit}>
              Salva e invia
            </IonButton>
          </div>
        </IonFooter>
      ) : null}
      <IonModal
        isOpen={isTemplatePickerOpen}
        onDidDismiss={() => {
          setIsTemplatePickerOpen(false)
          setTemplateSearchText("")
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Scheda di valutazione</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setIsTemplatePickerOpen(false)}>OK</IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              value={templateSearchText}
              debounce={200}
              placeholder="Cerca scheda di valutazione"
              cancelButtonText="Annulla"
              onIonChange={(event) => setTemplateSearchText(event.detail.value || "")}
            />
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonRadioGroup
            value={selectedTemplateId}
            onIonChange={(event) => setSelectedTemplateId(Number(event.detail.value))}
          >
            <IonList>
              {filteredTemplates.map((template) => (
                <IonItem key={template.id}>
                  <IonRadio slot="start" value={template.id} />
                  <IonLabel className="evaluation-picker-label">
                    <h2>{template.title}</h2>
                    <p>Versione {template.version}</p>
                    {template.description ? <p>{template.description}</p> : null}
                  </IonLabel>
                </IonItem>
              ))}
              {filteredTemplates.length === 0 ? (
                <div className="evaluation-empty">
                  Nessuna scheda trovata con questa ricerca.
                </div>
              ) : null}
            </IonList>
          </IonRadioGroup>
        </IonContent>
      </IonModal>
      <IonModal
        isOpen={isTraineePickerOpen}
        onDidDismiss={() => {
          setIsTraineePickerOpen(false)
          setTraineeSearchText("")
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Specializzando</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setIsTraineePickerOpen(false)}>OK</IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              value={traineeSearchText}
              debounce={200}
              placeholder="Cerca nome, nome utente o email"
              cancelButtonText="Annulla"
              onIonChange={(event) => setTraineeSearchText(event.detail.value || "")}
            />
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonRadioGroup
            value={selectedTraineeId}
            onIonChange={(event) => setSelectedTraineeId(Number(event.detail.value))}
          >
            <IonList>
              {filteredTrainees.map((trainee) => (
                <IonItem key={trainee.id}>
                  <IonRadio slot="start" value={trainee.id} />
                  <IonLabel>
                    <h2>{fullName(trainee)}</h2>
                    {trainee.email ? <p>{trainee.email}</p> : null}
                  </IonLabel>
                </IonItem>
              ))}
              {filteredTrainees.length === 0 ? (
                <div className="evaluation-empty">
                  Nessuno specializzando trovato con questa ricerca.
                </div>
              ) : null}
            </IonList>
          </IonRadioGroup>
        </IonContent>
      </IonModal>
      <IonToast
        isOpen={!!toastMessage}
        message={toastMessage}
        duration={2500}
        onDidDismiss={() => setToastMessage("")}
      />
    </IonPage>
  )
}

export default Evaluations
